// eslint-disable-next-line import-x/no-named-as-default -- use the SDK's default singleton.
import posthog from 'posthog-js';
import type { CaptureOptions, CaptureResult, Properties } from 'posthog-js';

import { ANALYTICS_TRAFFIC, DEVICE_INFO, DEVICE_UUID, RELEASE_VERSION } from './analytics-constants';
import { getCampaignParams } from './campaign-params';
import { isHuman } from './human-gate';

type AnalyticsUser = Readonly<{
  id: number;
  guest: boolean;
  is_staff?: boolean;
  email?: string;
  email_hash?: string;
  first_name?: string;
  last_name?: string;
  created_at?: string;
  country_code?: string;
  avatar?: Readonly<{ is_silhouette?: boolean; normal?: string }>;
  roles?: unknown;
  school_name?: string;
  school_type?: string;
  school_id?: number;
  paying_user?: boolean;
  user_type?: string;
  token_balance?: number;
  hubspot_contact_id?: string;
}>;

type Subscription =
  Readonly<{ status?: string; is_premed_plus?: boolean; subscription_started_at?: string }> | null | undefined;

type CampaignParams = ReturnType<typeof getCampaignParams>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let prevUpdateProps: Readonly<{ userProps?: Properties; regProps?: Properties }> = {};
// The Rails user this page is signed in as (numeric id as a string), or null for guests.
let expectedUserId: string | null = null;
// Campaign params from this page load; written to the person only once there is a person (identify).
let campaign: CampaignParams;
// Stand-in anonymous id for a guest tab whose shared cookie was taken over by an Admissions user.
let tabAnonymousId: string | null = null;
// Bumped by reset(), so async work started for one signed-in person cannot land after logout.
let identityGeneration = 0;
// Anonymous ids already merged back into the signed-in user: one $identify per Admissions reset.
const reclaimedIds = new Set<string>();
// Rails' signed auth cookie as it was when this tab identified its user. A reclaim only runs while it is
// unchanged, so a stale tab never claims an id created after a logout or a different user's login.
let authCookieAtIdentify: string | null = null;
// Staff see other people's emails and names (admin search, student records, rosters), so nothing of theirs is sent.
let staff = false;
let warned = false;

function warn(operation: string, error: unknown): void {
  if (warned) return;
  warned = true;
  // eslint-disable-next-line no-console
  if (__DEV__) console.warn(`PostHog ${operation} failed`, error);
}

function safely(operation: string, callback: () => void): void {
  try {
    callback();
  } catch (error) {
    warn(operation, error);
  }
}

const SECRET_INPUT_NAME = /card|cvc|cvv|csc|security|passw/i;

// Passwords and payment card fields are the only inputs masked in session replay.
export function isSecretInput(element?: HTMLElement): boolean {
  if (!element) return false;
  const type = element.getAttribute('type') ?? '';
  const autocomplete = element.getAttribute('autocomplete') ?? '';
  const name = `${element.getAttribute('name') ?? ''} ${element.id}`;
  return type === 'password' || autocomplete.startsWith('cc-') || SECRET_INPUT_NAME.test(name);
}

export function maskSecretInput(text: string, element?: HTMLElement): string {
  return isSecretInput(element) ? '*'.repeat(text.length) : text;
}

function safelyReturn<T>(operation: string, callback: () => T): T | undefined {
  try {
    return callback();
  } catch (error) {
    warn(operation, error);
    return undefined;
  }
}

function ready(): boolean {
  return Boolean(process.env.POSTHOG_PROJECT_KEY && posthog.__loaded);
}

function userProps(user: AnalyticsUser, subscription: Subscription): Properties {
  return {
    email: user.email,
    email_hash: user.email_hash,
    name: [user.first_name, user.last_name].filter(Boolean).join(' '),
    first_name: user.first_name,
    last_name: user.last_name,
    created_at: user.created_at,
    country_code: user.country_code,
    avatar: user.avatar?.is_silhouette ? undefined : user.avatar?.normal,
    roles: user.roles,
    school_name: user.school_name,
    school_type: user.school_type,
    school_id: user.school_id,
    subscription_status: subscription?.status,
    subscription_is_premed: subscription?.is_premed_plus,
    subscription_started_at: subscription?.subscription_started_at,
    paying_user: user.paying_user,
    token_balance: user.token_balance,
    hubspot_contact_id: user.hubspot_contact_id,
  };
}

function regProps(user: AnalyticsUser, subscription: Subscription): Properties {
  // Every key is always present (null when absent) so `register` overwrites whatever a previous
  // user or session left in this origin's localStorage. Mixpanel gets the same values.
  return {
    user_type: user.user_type ?? null,
    subscription_status: subscription?.status ?? null,
    subscription_is_premed: subscription?.is_premed_plus ?? null,
    paying_user: user.paying_user ?? null,
  };
}

function isForeignIdentity(distinctId: unknown): boolean {
  // Rails persons are numeric ids and anonymous visitors are UUIDs; anything else (today the
  // Admissions app's `wizeprep:user:<uuid>`) belongs to another identity system.
  return typeof distinctId !== 'string' || !(/^\d+$/.test(distinctId) || UUID_PATTERN.test(distinctId));
}

// Admissions swaps a signed-in Rails person for a fresh anonymous UUID in the shared cookie. install()
// merges it back on the next full page load; a tab left open or restored from the back-forward cache
// merges it on the first event that sees it. Only while Rails' auth cookie exists (deleted on every
// logout, config/initializers/warden_hooks.rb): after a logout elsewhere the id may be someone else.
function authCookie(): string | null {
  return document.cookie.split('; ').find(c => c.startsWith('user.id=')) ?? null;
}

function sameSignIn(): boolean {
  return authCookieAtIdentify !== null && authCookie() === authCookieAtIdentify;
}

function reclaimAnonymousId(anonymousId: string): void {
  if (reclaimedIds.has(anonymousId) || !sameSignIn()) return;
  reclaimedIds.add(anonymousId);
  const userId = expectedUserId;
  const generation = identityGeneration;
  // Deferred: this runs inside the SDK's capture. identify() from the anonymous state sends
  // $identify with $anon_distinct_id, which merges the two persons.
  setTimeout(() => {
    safely('reclaim', () => {
      if (!userId || !ready() || generation !== identityGeneration || userId !== expectedUserId || !sameSignIn())
        return;
      if (posthog.get_distinct_id() !== anonymousId || posthog.get_property('$user_state') === 'identified') return;
      posthog.register(ownContext); // heal persistence after the cookie swap
      posthog.identify(userId);
    });
  }, 0);
}

// Accepted edge: this stand-in id is not merged into the person at a later sign-in, so that guest
// history stays anonymous. Nothing lands on the wrong person; the guard only ever withholds.
function tabAnonymousIdentity(): string {
  tabAnonymousId ??= typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
  return tabAnonymousId;
}

// The persistence cookie is shared across *.wizeprep.com and re-read on every capture
// (`cookieWinsOnConflict`). Another tab signed in to Admissions can therefore swap this tab's
// distinct_id under it. Rewrite in place instead of touching persistence: no reset ping-pong
// between the two tabs and no merge of the two persons.
function keepOwnIdentity<E extends { properties?: Properties }>(event: E | null): E | null {
  if (!event?.properties) return event;
  const distinctId: unknown = event.properties.distinct_id;
  if (expectedUserId) {
    if (distinctId !== expectedUserId) {
      if (typeof distinctId === 'string' && UUID_PATTERN.test(distinctId)) reclaimAnonymousId(distinctId);
      event.properties.distinct_id = expectedUserId;
      event.properties.$is_identified = true;
    }
  } else if (isForeignIdentity(distinctId)) {
    event.properties.distinct_id = tabAnonymousIdentity();
    event.properties.$is_identified = false;
  }
  return event;
}

function writeCampaignPersonProps(): void {
  if (!campaign) return;
  const props = { $set: campaign.params, $set_once: campaign.firstParams };
  // Match setPersonProperties' flag cache update, but send its $set before a signup/login redirect.
  posthog.setPersonPropertiesForFlags(props, true);
  posthog.capture('$set', props, { send_instantly: true });
}

function dropStaffEvents<E>(event: E | null): E | null {
  return staff ? null : event;
}

// The SDK's own events bypass `whenHuman`, which holds everything this app captures.
const AUTOMATIC_EVENTS = new Set([
  '$autocapture',
  '$copy_autocapture',
  '$dead_click',
  '$pageleave',
  '$rageclick',
  '$web_vitals',
]);
function dropAutomaticEventsBeforeHuman<E extends { event: string }>(event: E | null): E | null {
  return event && !isHuman() && AUTOMATIC_EVENTS.has(event.event) ? null : event;
}

// Everything this tab registered. An Admissions reset rewrites the shared cookie, and with
// cookieWinsOnConflict the SDK then drops these super properties until the next register.
let ownContext: Properties = {};
const NO_USER_REG_PROPS: Properties = {
  user_type: null,
  subscription_status: null,
  subscription_is_premed: null,
  paying_user: null,
};

function registerOwn(props: Properties): void {
  ownContext = { ...ownContext, ...props };
  posthog.register(props);
}

// Refills keys the SDK lost (see ownContext); never overrides a value the event already has.
function restoreOwnContext<E extends { properties?: Properties }>(event: E | null): E | null {
  if (!event?.properties) return event;
  for (const [key, value] of Object.entries(ownContext) as [string, unknown][]) {
    if (event.properties[key] === undefined) event.properties[key] = value;
  }
  return event;
}

function sessionProps(): Properties {
  return {
    analytics_source: ANALYTICS_TRAFFIC.source,
    analytics_traffic_type: ANALYTICS_TRAFFIC.trafficType,
    analytics_version: ANALYTICS_TRAFFIC.version,
    user_agent: DEVICE_INFO.ua,
    device_uuid: DEVICE_UUID,
    release_version: RELEASE_VERSION,
  };
}

export function install(user: AnalyticsUser, subscription: Subscription): void {
  const projectKey = process.env.POSTHOG_PROJECT_KEY;
  if (!projectKey) return;
  staff = Boolean(user.is_staff);

  safely('install', () => {
    posthog.init(projectKey, {
      api_host: process.env.POSTHOG_API_HOST || 'https://us.i.posthog.com',
      ui_host: 'https://us.posthog.com',
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: true,
      cookieWinsOnConflict: true,
      person_profiles: 'identified_only',
      autocapture: true,
      capture_pageview: false,
      capture_pageleave: true,
      // Replay on, except for staff. Typed input stays readable (search boxes, forms) except passwords and card fields:
      // two checkout forms type card details into plain inputs (Stripe Elements are iframes and are
      // never recorded). `maskInputFn` only runs for inputs `maskAllInputs` selects, i.e. all of them.
      // Elements showing other people's emails carry `ph-no-capture`, which autocapture and replay both skip.
      disable_session_recording: staff,
      session_recording: { maskAllInputs: true, maskInputFn: maskSecretInput },
      respect_dnt: false,
      debug: false,
      before_send: [dropStaffEvents, dropAutomaticEventsBeforeHuman, restoreOwnContext, keepOwnIdentity],
      loaded: ph => {
        safely('loaded', () => {
          // The persistence cookie is shared across *.wizeprep.com with the Admissions app. Any
          // identified id that is not this Rails user (another Rails user, or a Platform user) is
          // reset so their person never inherits this visit; anonymous ids are kept and merged.
          const distinctId = ph.get_distinct_id();
          const identified = ph.get_property('$user_state') === 'identified';
          if (identified && (user.guest || distinctId !== String(user.id))) {
            ph.reset();
            // Recorded under the new anonymous id, never under the foreign person.
            ph.capture('PH Session Auto-Reset', { prev_distinct_id: distinctId, user_id: user.id });
          }
        });
      },
    });
  });
  if (!ready()) return;

  safely('install', () => {
    const nextUserProps = userProps(user, subscription);
    const nextRegProps = regProps(user, subscription);
    campaign = getCampaignParams();
    expectedUserId = user.guest ? null : String(user.id);
    authCookieAtIdentify = expectedUserId ? authCookie() : null;
    // Identify before any person-property write so anonymous visitors never get a person profile.
    // Guests' campaign params wait in `campaign` until they sign up (Mixpanel queues people.set the same way).
    if (expectedUserId) {
      posthog.identify(expectedUserId, nextUserProps);
      writeCampaignPersonProps();
    }
    // PostHog records $initial_* campaign props itself; the Mixpanel-named copies are for parity.
    if (campaign) registerOwn(campaign.params);
    // Persistent super properties, re-registered on every page load and cleared by reset(); a
    // session-scoped register would silently drop them after 30 minutes idle in an open tab.
    registerOwn({ ...sessionProps(), ...nextRegProps });
    prevUpdateProps = { userProps: nextUserProps, regProps: nextRegProps };
  });
}

export function update(user: AnalyticsUser, subscription: Subscription): void {
  if (!ready()) return;

  safely('update', () => {
    if (user.guest) return;
    const nextUserProps = userProps(user, subscription);
    const nextRegProps = regProps(user, subscription);
    const becameUser = expectedUserId !== String(user.id);
    expectedUserId = String(user.id);
    // Every update, not only on a user change: a login calls identify() before update(), so becameUser is false.
    staff = Boolean(user.is_staff);
    if (staff) posthog.stopSessionRecording();
    if (becameUser) authCookieAtIdentify = authCookie();
    // Before identify, so the $identify and later events never carry the previous user's properties.
    if (JSON.stringify(nextRegProps) !== JSON.stringify(prevUpdateProps.regProps)) {
      registerOwn(nextRegProps);
    }
    if (becameUser || JSON.stringify(nextUserProps) !== JSON.stringify(prevUpdateProps.userProps)) {
      posthog.identify(expectedUserId, nextUserProps);
    }
    if (becameUser) writeCampaignPersonProps();
    prevUpdateProps = { userProps: nextUserProps, regProps: nextRegProps };
  });
}

export function pageview({ initial_load }: Readonly<{ initial_load: boolean }>): void {
  if (!ready()) return;
  safely('pageview', () => posthog.capture('$pageview', { initial_load }));
}

export function identify(userId: number): void {
  if (!ready()) return;
  safely('identify', () => {
    const becameUser = expectedUserId !== String(userId);
    expectedUserId = String(userId);
    if (becameUser) {
      authCookieAtIdentify = authCookie();
      // update() registers this user's own values next; until then nothing of the previous user's.
      registerOwn(NO_USER_REG_PROPS);
      prevUpdateProps = {};
    }
    posthog.identify(expectedUserId);
    // The visitor now has a person: land the campaign params queued since page load on it.
    if (becameUser) writeCampaignPersonProps();
  });
}

export function reset(): void {
  if (!ready()) return;
  safely('reset', () => {
    posthog.reset();
    // reset() clears every super property; the tab's traffic tags still apply to the guest that follows.
    ownContext = {};
    registerOwn(sessionProps());
    identityGeneration += 1;
    expectedUserId = null;
    authCookieAtIdentify = null;
    staff = false;
    prevUpdateProps = {};
    reclaimedIds.clear();
  });
}

export function register(props: Properties): void {
  if (!ready()) return;
  safely('register', () => registerOwn(props));
}

function captureOptions(sendInstantly: boolean, beacon: boolean): CaptureOptions | undefined {
  if (beacon) return { send_instantly: true, transport: 'sendBeacon' };
  return sendInstantly ? { send_instantly: true } : undefined;
}

export function capture(
  event: string,
  props: Properties,
  { beacon = false, sendInstantly = false } = {},
): CaptureResult | undefined {
  if (!ready()) return;
  // `sendInstantly` mirrors Mixpanel's send_immediately for events followed by a redirect; `beacon` is for one
  // sent as the page hides.
  return safelyReturn('capture', () => posthog.capture(event, props, captureOptions(sendInstantly, beacon)));
}

export function trackingIdentity(): Readonly<{ distinctId: string; sessionId: string }> | undefined {
  if (!ready() || staff || !ANALYTICS_TRAFFIC.enabled) return;
  return safelyReturn('trackingIdentity', () => {
    if (posthog.has_opted_out_capturing()) return;
    const event = keepOwnIdentity({ properties: { distinct_id: posthog.get_distinct_id() } });
    if (!event) return;
    return { distinctId: event.properties.distinct_id, sessionId: posthog.get_session_id() };
  });
}
